import { toDataURL } from "qrcode";

import { readIdentityHint } from "@mightybot/core";

import { isBuiltDomain, isPlatformDomain } from "../../utils/helpers";

import type { AuthMode } from "./types";
import type { SignupBillingOnboarding, User } from "@mightybot/core";

export const DEFAULT_AUTH_ERROR_MESSAGE =
	"We couldn't complete the request. Please try again.";

export const PASSWORD_REQUIREMENTS_MESSAGE =
	"Use at least 12 characters with lowercase, uppercase, number, and symbol.";

export const getDefaultMode = (pathname: string, search: string): AuthMode => {
	const params = new URLSearchParams(search);
	if (params.get("mode") === "reset-password") {
		return "resetPassword";
	}
	if (params.get("mode") === "forgot-password") {
		return "forgotPassword";
	}
	if (params.get("mode") === "trial-tasks") {
		return "signupTrialTasks";
	}
	if (params.get("mode") === "payment-required") {
		return "signupPaymentRequired";
	}
	if (params.has("token") || params.has("code")) {
		return "resetPassword";
	}

	if (pathname.includes("/signup")) {
		return "signup";
	}

	// Signin path: if we recognize this browser (identity hint persisted
	// from a prior successful /me hydration), open the WelcomeBackScreen
	// account chooser instead of the generic signin form. Hint is cleared
	// by explicit logout; preserved across session expiry — see
	// services/identityHint.ts for the full rules.
	if (readIdentityHint()) {
		return "welcomeBack";
	}

	return "signin";
};

// Modes that mean "the user is trying to start a session": the generic sign-in
// form, the signup form, or the WelcomeBack recognition chooser. When an
// already-authenticated user (valid /me) lands on one of these — e.g. a
// logged-in user opening /login, which resolves to `welcomeBack` — they should
// be bounced into the app instead of shown a re-login screen.
//
// Mid-flow and error modes (verify / reset / 2FA / recovery / securitySetup /
// domainRejected / rateLimited / serverError) are deliberately excluded: an
// authenticated user who followed an emailed reset or verify link (opened in
// the browser they're already signed into) must be allowed to finish the flow.
const AUTH_ENTRY_MODES: ReadonlySet<AuthMode> = new Set<AuthMode>([
	"signin",
	"signup",
	"welcomeBack",
]);

export const isAuthEntryMode = (mode: AuthMode): boolean =>
	AUTH_ENTRY_MODES.has(mode);

export const getApiStatus = (error: unknown): number | string | undefined => {
	const errorRecord = getObjectRecord(error);
	if (!errorRecord) {
		return undefined;
	}

	const status = errorRecord.status;
	return typeof status === "number" || typeof status === "string"
		? status
		: undefined;
};

export const getApiProvider = (error: unknown): string | null => {
	const errorRecord = getObjectRecord(error);
	if (!errorRecord) return null;
	const dataRecord = getObjectRecord(errorRecord.data);
	const innerData = getObjectRecord(dataRecord?.data);
	return (
		getStringValue(innerData?.provider) || getStringValue(dataRecord?.provider)
	);
};

export const getApiMessage = (error: unknown): string => {
	const errorRecord = getObjectRecord(error);
	if (!errorRecord) {
		return DEFAULT_AUTH_ERROR_MESSAGE;
	}

	const dataRecord = getObjectRecord(errorRecord.data);
	const message =
		getStringValue(dataRecord?.message) ||
		getStringValue(dataRecord?.error) ||
		getStringValue(dataRecord?.description) ||
		getStringValue(dataRecord?.detail) ||
		getStringValue(errorRecord.error);
	return message || DEFAULT_AUTH_ERROR_MESSAGE;
};

// Stable code the backend returns (HTTP 403) when a business email's domain is
// not on the signup allowlist — distinct from the personal-email block, which
// keeps its own "use a work email" screen. Kept in sync with
// chatapp/services/auth/signup_policy.py::SIGNUP_NOT_ALLOWED_CODE.
export const SIGNUP_NOT_ALLOWED_CODE = "signup_domain_not_allowed";

// Fallback sales contact link used if the backend response omits one.
export const CONTACT_SALES_URL =
	"https://calendly.com/mightybot-john/mightybot-agent-platform-demo?background_color=ffffff&text_color=000000&primary_color=0554f2";

// Shared "request access" copy, so the inline card (RequestAccessNotice) and the
// full-screen OAuth screen (AuthFlow) can't drift.
export const REQUEST_ACCESS_TITLE = "We don't recognize this email yet";
export const REQUEST_ACCESS_BODY =
	"MightyBot is rolling out by invitation — our team can add your email so you can sign up.";
export const REQUEST_ACCESS_ACTION = "Connect with our sales team";

export const isSignupNotAllowedError = (error: unknown): boolean => {
	const errorRecord = getObjectRecord(error);
	const dataRecord = getObjectRecord(errorRecord?.data);
	return getStringValue(dataRecord?.code) === SIGNUP_NOT_ALLOWED_CODE;
};

export const getContactSalesUrl = (error: unknown): string | null => {
	const errorRecord = getObjectRecord(error);
	const dataRecord = getObjectRecord(errorRecord?.data);
	return getStringValue(dataRecord?.contact_sales_url);
};

export const getContactSalesMessage = (error: unknown): string | null => {
	const message = getApiMessage(error);
	return message === DEFAULT_AUTH_ERROR_MESSAGE ? null : message;
};

export interface FriendlyAuthError {
	message: string;
	actionLabel?: string;
}

// Backend auth errors are terse, lowercase, developer-facing strings. Map the
// ones users actually hit to friendly copy (with an optional inline action), and
// fall back to a sentence-cased version of anything unmapped so nothing shows up
// as a bare lowercase fragment.
const FRIENDLY_AUTH_MESSAGES: Record<string, FriendlyAuthError> = {
	"account already exists": {
		message: "An account with this email already exists. Sign in instead.",
		actionLabel: "Sign in",
	},
	"invalid email or password": {
		message: "That email or password isn't right. Please try again.",
	},
	"invalid email": { message: "Please enter a valid email address." },
	"email is not verified": {
		message: "Please verify your email address before continuing.",
	},
	"verification code expired": {
		message: "That code has expired. Request a new one to continue.",
	},
	"invalid verification code": {
		message: "That code isn't right. Double-check it and try again.",
	},
	"verification code must be 6 digits": {
		message: "Enter the 6-digit code from your email.",
	},
};

const toSentenceCase = (text: string): string =>
	text ? text.charAt(0).toUpperCase() + text.slice(1) : text;

export const mapAuthError = (error: unknown): FriendlyAuthError => {
	const raw = getApiMessage(error);
	const mapped = FRIENDLY_AUTH_MESSAGES[raw.trim().toLowerCase()];
	if (mapped) {
		return mapped;
	}
	if (raw === DEFAULT_AUTH_ERROR_MESSAGE) {
		return { message: raw };
	}
	return { message: toSentenceCase(raw) };
};

export const getObjectRecord = (
	value: unknown,
): Record<string, unknown> | null => {
	if (typeof value !== "object" || value === null || Array.isArray(value)) {
		return null;
	}

	return Object.fromEntries(Object.entries(value));
};

const getStringValue = (value: unknown): string | null => {
	if (typeof value !== "string") {
		return null;
	}
	const trimmed = value.trim();
	return trimmed || null;
};

export const getHomePath = () => {
	if (isBuiltDomain()) {
		return "/dashboard";
	}

	if (isPlatformDomain()) {
		return "/workflows";
	}

	return "/settings/general";
};

export const getSignupTrialTasksPath = () => "/signup?mode=trial-tasks";
export const getSignupPaymentRequiredPath = () =>
	"/signup?mode=payment-required";

export const getSignupPaymentRequiredReturnPath = () => {
	const basePath =
		typeof window !== "undefined" &&
		window.location.pathname.startsWith("/app/")
			? "/app"
			: "";
	return `${basePath}${getSignupPaymentRequiredPath()}`;
};

export const getPendingSignupOnboardingMode = (
	user:
		| Pick<User, "onboarding_completed" | "onboarding_status">
		| null
		| undefined,
): AuthMode | null => {
	if (!user || user.onboarding_completed !== false) {
		return null;
	}
	const step = user.onboarding_status?.current_step;
	if (step === "trial_tasks") {
		return "signupTrialTasks";
	}
	if (step === "request_access") {
		return "requestAccess";
	}
	return "signupPaymentRequired";
};

export const getPendingSignupOnboardingPath = (
	user:
		| Pick<User, "onboarding_completed" | "onboarding_status">
		| null
		| undefined,
): string | null => {
	const mode = getPendingSignupOnboardingMode(user);
	if (mode === "signupTrialTasks") {
		return getSignupTrialTasksPath();
	}
	if (mode === "requestAccess") {
		return "/signup";
	}
	if (mode === "signupPaymentRequired") {
		return getSignupPaymentRequiredPath();
	}
	return null;
};

/**
 * Decide whether the /me-driven effect should route the user to the signup
 * onboarding screen /me implies. Returns the mode to route to, or null to stay
 * put.
 *
 * It stays put when /me merely *lags* an authoritative login decision: after the
 * login-response decision replay puts a freshly-approved user on trial-granted,
 * the identity façade (platform-dev) can briefly serve a pre-replay /me still
 * reading `request_access`. Routing on that stale value bounces the user to
 * sales-required until they refresh. So when /me disagrees with the mode a login
 * decision just established, keep the login decision. A URL-driven mode
 * (deep link) has no authoritative mode, so /me still corrects it.
 */
export const resolvePendingSignupRoute = ({
	currentMode,
	pendingSignupMode,
	authoritativeSignupMode,
}: {
	currentMode: AuthMode;
	pendingSignupMode: AuthMode | null;
	authoritativeSignupMode: AuthMode | null;
}): AuthMode | null => {
	if (!pendingSignupMode) {
		return null;
	}
	const onSyncableScreen =
		isAuthEntryMode(currentMode) ||
		currentMode === "signupPaymentRequired" ||
		currentMode === "signupTrialTasks";
	if (!onSyncableScreen) {
		return null;
	}
	if (
		pendingSignupMode !== currentMode &&
		authoritativeSignupMode === currentMode
	) {
		return null;
	}
	return pendingSignupMode;
};

const REQUEST_ACCESS_SIGNUP_DECISIONS = new Set([
	"request_access",
	"sales_required",
]);

export const isSignupPaymentRequired = (
	billing: SignupBillingOnboarding | null | undefined,
): boolean => {
	if (!billing) {
		return true;
	}
	return (
		billing.payment_method_required ||
		billing.signup_decision === "payment_required"
	);
};

export const isSignupRequestAccessRequired = (
	billing: SignupBillingOnboarding | null | undefined,
): boolean =>
	Boolean(
		billing?.request_access_required ||
		(billing && REQUEST_ACCESS_SIGNUP_DECISIONS.has(billing.signup_decision)),
	);

export const getDisplayName = (email: string) => {
	const [namePart] = email.split("@");
	return namePart ? namePart.replace(/[._-]+/g, " ") : undefined;
};

export const getEmailDomain = (email: string): string | null => {
	const parts = email.trim().toLowerCase().split("@");
	return parts.length === 2 ? parts[1].replace(/\.$/, "") : null;
};

export const isBlockedSignupDomain = (
	email: string,
	blockedDomains: string[],
): boolean => {
	const domain = getEmailDomain(email);
	return Boolean(domain && blockedDomains.includes(domain));
};

export const isValidEmail = (email: string) => /\S+@\S+\.\S+/.test(email);

export const MIN_PASSWORD_LENGTH = 12;

export const isStrongPassword = (password: string) =>
	password.length >= MIN_PASSWORD_LENGTH &&
	/[a-z]/.test(password) &&
	/[A-Z]/.test(password) &&
	/\d/.test(password) &&
	/[^A-Za-z0-9]/.test(password);

export type PasswordStrength = 0 | 1 | 2 | 3;

export interface PasswordScore {
	strength: PasswordStrength;
	label: string;
	hint: string;
	isStrong: boolean;
}

export const scorePassword = (password: string): PasswordScore => {
	if (!password) {
		return { strength: 0, label: "", hint: "", isStrong: false };
	}

	const hasLower = /[a-z]/.test(password);
	const hasUpper = /[A-Z]/.test(password);
	const hasDigit = /\d/.test(password);
	const hasSymbol = /[^A-Za-z0-9]/.test(password);
	const longEnough = password.length >= MIN_PASSWORD_LENGTH;
	const isStrong = longEnough && hasLower && hasUpper && hasDigit && hasSymbol;

	const remaining = MIN_PASSWORD_LENGTH - password.length;
	let hint: string;
	if (!longEnough) {
		hint =
			remaining === 1
				? "Just 1 more character — almost there."
				: `${remaining} more characters to reach ${MIN_PASSWORD_LENGTH}.`;
	} else if (!hasLower) {
		hint = "Mix in a lowercase letter (a–z).";
	} else if (!hasUpper) {
		hint = "Add an uppercase letter (A–Z) for extra strength.";
	} else if (!hasDigit) {
		hint = "Toss in a number (0–9).";
	} else if (!hasSymbol) {
		hint = "Finish with a symbol — try !, @, #, or $.";
	} else {
		hint = "Nice — you're all set.";
	}

	if (isStrong) {
		return { strength: 3, label: "Strong", hint, isStrong: true };
	}

	const classes = [hasLower, hasUpper, hasDigit, hasSymbol].filter(
		Boolean,
	).length;
	const meetsHalf = password.length >= MIN_PASSWORD_LENGTH / 2 && classes >= 2;
	if (meetsHalf) {
		return { strength: 2, label: "Fair", hint, isStrong: false };
	}
	return { strength: 1, label: "Weak", hint, isStrong: false };
};

export const canRenderQrImage = (qr: string) =>
	qr.startsWith("data:image/") ||
	qr.startsWith("https://") ||
	qr.startsWith("http://");

export const createTotpQrImageSrc = async (
	uri: string,
	qr?: string | null,
): Promise<string> => {
	const providedQr = qr?.trim() || "";
	if (providedQr && canRenderQrImage(providedQr)) {
		return providedQr;
	}

	const trimmedUri = uri.trim();
	if (!trimmedUri) {
		return "";
	}

	try {
		return await toDataURL(trimmedUri, {
			errorCorrectionLevel: "M",
			margin: 2,
			width: 224,
			color: {
				dark: "#0f172aff",
				light: "#ffffffff",
			},
		});
	} catch {
		return "";
	}
};

export const formatCountdown = (totalSeconds: number): string => {
	const safe = Math.max(0, Math.floor(totalSeconds));
	const minutes = Math.floor(safe / 60);
	const seconds = safe % 60;
	return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

export const groupSecret = (secret: string, groupSize = 4): string =>
	(secret.match(new RegExp(`.{1,${groupSize}}`, "g")) ?? []).join(" ");

export const isAuthRoute = () => {
	if (isPlatformDomain()) {
		return ["/login", "/signin", "/signup"].includes(window.location.pathname);
	}

	return ["/app/login", "/app/signin", "/app/auth", "/app/signup"].includes(
		window.location.pathname,
	);
};
